import { useState, useEffect, useMemo } from 'react';

function formatProductPriceForSelect(p) {
    const raw = p?.price;
    const n = parseFloat(raw);
    if (raw === undefined || raw === null || Number.isNaN(n)) return '—';
    return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₺`;
}
import { apiRequest, getImageUrl } from '../../utils/api';
import SearchableSelect from './SearchableSelect';

const CampaignFormModal = ({ isOpen, onClose, campaign, organizationId, onSuccess }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        campaign_type: 'discount',
        discount_type: 'percentage',
        discount_value: '',
        combo_price: '',
        buy_quantity: '',
        get_quantity: '',
        get_discount_type: 'free',
        get_discount_value: '',
        product_id: '',
        category_id: '',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        active_days: [],
        status: 'active',
        // New fields
        campaign_image: null,
        quantity_discount_type: 'unit_price',
        quantity_rules: [],
        combination_rules: [],
        trigger_product_id: '',
        trigger_category_id: '',
        target_product_id: '',
        target_category_id: '',
        product_ids: [],
        daily_schedule: {}
    });
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [media, setMedia] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [campaignImage, setCampaignImage] = useState(null);
    const [campaignImagePreview, setCampaignImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedCategoryTree, setSelectedCategoryTree] = useState(null);

    const productsForSelect = useMemo(() => (
        products.map(p => ({
            ...p,
            name: `${p.name} — ${formatProductPriceForSelect(p)}`
        }))
    ), [products]);

    const days = [
        { value: 'monday', label: 'Pazartesi' },
        { value: 'tuesday', label: 'Salı' },
        { value: 'wednesday', label: 'Çarşamba' },
        { value: 'thursday', label: 'Perşembe' },
        { value: 'friday', label: 'Cuma' },
        { value: 'saturday', label: 'Cumartesi' },
        { value: 'sunday', label: 'Pazar' }
    ];

    useEffect(() => {
        loadProductsAndCategories();
    }, [organizationId]);

    useEffect(() => {
        if (campaign) {
            // Parse combination_rules
            let combinationRules = [];
            if (campaign.combination_rules) {
                if (Array.isArray(campaign.combination_rules)) {
                    combinationRules = campaign.combination_rules;
                } else if (typeof campaign.combination_rules === 'string' && campaign.combination_rules.trim() !== '') {
                    try {
                        combinationRules = JSON.parse(campaign.combination_rules);
                    } catch (e) {
                        combinationRules = [];
                    }
                }
            }
            
            // If combination_rules is empty but we have products, build from products
            if (combinationRules.length === 0 && campaign.products && campaign.products.length > 0) {
                combinationRules = campaign.products.map(p => ({
                    product_id: p.product_id || p.id,
                    min_quantity: p.min_quantity || 1
                }));
            }
            
            // Build product_ids from products or combination_rules
            let productIds = [];
            if (campaign.products && campaign.products.length > 0) {
                productIds = campaign.products.map(p => ({
                    id: p.product_id || p.id,
                    min_quantity: p.min_quantity || 1,
                    display_quantity: p.display_quantity || p.min_quantity || 1
                }));
            } else if (combinationRules.length > 0) {
                productIds = combinationRules.map(rule => ({
                    id: rule.product_id,
                    min_quantity: rule.min_quantity || 1,
                    display_quantity: rule.min_quantity || 1
                }));
            }
            
            // For combination campaigns, determine pricing type
            const isCombination = campaign.campaign_type === 'combination';
            const hasComboPrice = campaign.combo_price && campaign.combo_price !== '' && campaign.combo_price !== '0';
            
            setFormData({
                title: campaign.title || '',
                description: campaign.description || '',
                campaign_type: campaign.campaign_type || 'discount',
                // For combination campaigns: if combo_price exists, clear discount; if discount exists, clear combo_price
                discount_type: (isCombination && hasComboPrice) ? '' : (campaign.discount_type || ''),
                discount_value: (isCombination && hasComboPrice) ? '' : (campaign.discount_value || ''),
                combo_price: campaign.combo_price || '',
                buy_quantity: campaign.buy_quantity || '',
                get_quantity: campaign.get_quantity || '',
                get_discount_type: campaign.get_discount_type || 'free',
                get_discount_value: campaign.get_discount_value || '',
                product_id: campaign.product_id || '',
                category_id: campaign.category_id || '',
                start_date: campaign.start_date ? campaign.start_date.split('T')[0] : '',
                end_date: campaign.end_date ? campaign.end_date.split('T')[0] : '',
                start_time: campaign.start_time ? (campaign.start_time.includes(':') ? campaign.start_time.substring(0, 5) : campaign.start_time) : '',
                end_time: campaign.end_time ? (campaign.end_time.includes(':') ? campaign.end_time.substring(0, 5) : campaign.end_time) : '',
                active_days: campaign.active_days ? (Array.isArray(campaign.active_days) ? campaign.active_days : (typeof campaign.active_days === 'string' ? JSON.parse(campaign.active_days) : [])) : [],
                status: campaign.status || 'active',
                // New fields
                campaign_image: campaign.campaign_image || null,
                quantity_discount_type: campaign.quantity_discount_type || 'unit_price',
                quantity_rules: (() => {
                    // Parse quantity_rules - preserve all existing values
                    let rules = campaign.quantity_rules ? (Array.isArray(campaign.quantity_rules) ? campaign.quantity_rules : (typeof campaign.quantity_rules === 'string' ? JSON.parse(campaign.quantity_rules) : [])) : [];
                    
                    console.log('🔍 Campaign quantity_rules from backend:', JSON.stringify(campaign.quantity_rules, null, 2));
                    console.log('📋 Parsed quantity_rules:', JSON.stringify(rules, null, 2));
                    console.log('📦 Campaign products:', campaign.products);
                    
                    // For quantity_discount campaigns, add product_id to rules that don't have it
                    // IMPORTANT: Preserve all existing values (min_quantity, unit_price, total_price, buy_quantity, get_quantity)
                    if (campaign.campaign_type === 'quantity_discount' && campaign.products && campaign.products.length > 0) {
                        // Create a map of product_id to rule index
                        const productIdToRuleIndex = {};
                        rules.forEach((rule, index) => {
                            if (rule.product_id) {
                                productIdToRuleIndex[rule.product_id] = index;
                            }
                        });
                        
                        // Add product_id to rules that don't have it, preserving all other values
                        campaign.products.forEach((product, productIndex) => {
                            const productId = product.product_id || product.id;
                            if (productId) {
                                if (productIdToRuleIndex[productId] !== undefined) {
                                    // Rule already has this product_id, do nothing (preserve all values)
                                } else {
                                    // Find a rule without product_id at the same index
                                    if (productIndex < rules.length && !rules[productIndex].product_id) {
                                        // Add product_id to existing rule, preserve ALL other values
                                        rules[productIndex].product_id = productId;
                                    } else if (productIndex < rules.length && rules[productIndex].product_id) {
                                        // Rule at this index has a different product_id, skip
                                        // Don't overwrite existing rule
                                    } else {
                                        // No rule at this index, add new rule only if we have more products than rules
                                        if (productIndex >= rules.length) {
                                            rules.push({
                                                product_id: productId,
                                                min_quantity: product.min_quantity || 1,
                                                unit_price: null,
                                                total_price: null,
                                                buy_quantity: null,
                                                get_quantity: null
                                            });
                                        }
                                    }
                                }
                            }
                        });
                    }
                    
                    console.log('✅ Final quantity_rules for form:', JSON.stringify(rules, null, 2));
                    
                    // Return rules as-is, preserving all existing values
                    return rules;
                })(),
                combination_rules: combinationRules,
                trigger_product_id: campaign.trigger_product_id || '',
                trigger_category_id: campaign.trigger_category_id || '',
                target_product_id: campaign.target_product_id || '',
                target_category_id: campaign.target_category_id || '',
                product_ids: productIds,
                daily_schedule: campaign.daily_schedule ? (typeof campaign.daily_schedule === 'string' ? JSON.parse(campaign.daily_schedule) : campaign.daily_schedule) : {}
            });
            setMediaPreview(campaign.media_url || null);
            setCampaignImagePreview(campaign.campaign_image || null);
        } else {
            resetForm();
        }
    }, [campaign, isOpen]);

    const loadProductsAndCategories = async () => {
        try {
            const [productsResponse, categoriesResponse] = await Promise.all([
                apiRequest(`/organizations/${organizationId}/products?include_images=true`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${organizationId}/categories`).catch(() => ({ data: [] }))
            ]);
            // Add first image to products for SearchableSelect
            const productsWithImage = (productsResponse.data || []).map(product => {
                const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                return {
                    ...product,
                    image_url: primaryImage?.image_url || null
                };
            });
            console.log('📦 Products loaded:', productsWithImage.length, 'with images:', productsWithImage.filter(p => p.image_url).length);
            setProducts(productsWithImage);
            setCategories(categoriesResponse.data || []);
        } catch (error) {
            console.error('Load products/categories error:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            campaign_type: 'discount',
            discount_type: 'percentage',
            discount_value: '',
            combo_price: '',
            buy_quantity: '',
            get_quantity: '',
            get_discount_type: 'free',
            get_discount_value: '',
            product_id: '',
            category_id: '',
            start_date: '',
            end_date: '',
            start_time: '',
            end_time: '',
            active_days: [],
            status: 'active',
            campaign_image: null,
            quantity_discount_type: 'unit_price',
            quantity_rules: [],
            combination_rules: [],
            trigger_product_id: '',
            trigger_category_id: '',
            target_product_id: '',
            target_category_id: '',
            product_ids: [],
            daily_schedule: {}
        });
        setMedia(null);
        setMediaPreview(null);
        setCampaignImage(null);
        setCampaignImagePreview(null);
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // If campaign_type changes to 'combination', set default to 'fixed_price'
        if (name === 'campaign_type' && value === 'combination') {
            setFormData(prev => ({
                ...prev,
                campaign_type: value,
                combo_price: prev.combo_price || '', // Keep existing or leave empty for user to fill
                discount_type: '', // Clear discount_type
                discount_value: '' // Clear discount_value
            }));
            return;
        }
        
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDayToggle = (day) => {
        setFormData(prev => ({
            ...prev,
            active_days: prev.active_days.includes(day)
                ? prev.active_days.filter(d => d !== day)
                : [...prev.active_days, day]
        }));
    };

    const handleMediaChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
        if (!validTypes.includes(file.type)) {
            setError('Geçersiz dosya formatı. Sadece JPG, PNG, GIF, MP4 veya WebM formatları desteklenir.');
            e.target.value = ''; // Reset input
            return;
        }

        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            setError('Dosya boyutu çok büyük. Maksimum 10MB olmalıdır.');
            e.target.value = ''; // Reset input
            return;
        }

        setError(null);
        setMedia(file);
        const reader = new FileReader();
        reader.onerror = () => {
            setError('Dosya okunurken bir hata oluştu.');
        };
        reader.onloadend = () => {
            setMediaPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Build product_ids array based on campaign type
            let productIds = [];
            
            if (formData.campaign_type === 'quantity_discount' && formData.quantity_rules) {
                // For quantity_discount, extract product_ids from quantity_rules
                productIds = formData.quantity_rules
                    .filter(rule => rule.product_id && rule.product_id > 0)
                    .map(rule => parseInt(rule.product_id));
            } else if (formData.campaign_type === 'combination' && formData.combination_rules) {
                // For combination, extract product_ids from combination_rules
                productIds = formData.combination_rules
                    .filter(rule => rule.product_id && rule.product_id > 0)
                    .map(rule => ({
                        id: parseInt(rule.product_id),
                        min_quantity: rule.min_quantity || 1,
                        display_quantity: rule.min_quantity || 1
                    }));
            } else if (formData.product_ids && formData.product_ids.length > 0) {
                // Use existing product_ids if available
                productIds = formData.product_ids;
            } else if (formData.product_id) {
                // For other types, use product_id if available
                productIds = [parseInt(formData.product_id)];
            }

            const formDataToSend = new FormData();
            
            // Debug: Log quantity_rules before sending
            console.log('📤 Sending quantity_rules:', JSON.stringify(formData.quantity_rules, null, 2));
            console.log('📤 quantity_rules length:', formData.quantity_rules?.length);
            console.log('📤 quantity_rules has values:', formData.quantity_rules?.some(rule => 
                rule.unit_price || rule.total_price || rule.buy_quantity || rule.get_quantity
            ));
            
            Object.keys(formData).forEach(key => {
                if (key === 'active_days' || key === 'quantity_rules' || key === 'combination_rules' || key === 'daily_schedule') {
                    // For quantity_rules, always send if it exists and has at least one rule with product_id
                    if (key === 'quantity_rules') {
                        if (formData[key] && Array.isArray(formData[key]) && formData[key].length > 0) {
                            // Check if at least one rule has a product_id
                            const hasProductIds = formData[key].some(rule => rule.product_id && rule.product_id > 0);
                            if (hasProductIds) {
                                console.log('✅ Sending quantity_rules:', JSON.stringify(formData[key], null, 2));
                    formDataToSend.append(key, JSON.stringify(formData[key]));
                            } else {
                                console.log('⚠️ quantity_rules has no product_ids, skipping');
                            }
                        }
                    } else if (formData[key] && (Array.isArray(formData[key]) ? formData[key].length > 0 : Object.keys(formData[key]).length > 0)) {
                        formDataToSend.append(key, JSON.stringify(formData[key]));
                    }
                } else if (key === 'product_ids') {
                    // Skip - we'll add it separately below
                } else if (key === 'start_date' || key === 'end_date') {
                    // Boş string ise gönderme (null olarak kalacak)
                    if (formData[key] && formData[key].trim() !== '') {
                        formDataToSend.append(key, formData[key]);
                    }
                } else if (key === 'combo_price') {
                    // combo_price boş string veya '0' ise gönderme
                    if (formData[key] && formData[key] !== '' && formData[key] !== '0') {
                        formDataToSend.append(key, formData[key]);
                    }
                } else if (key === 'start_time' || key === 'end_time') {
                    // Zaman formatını HH:MM:SS'den HH:MM'e çevir
                    if (formData[key] && formData[key].trim() !== '') {
                        const timeValue = formData[key].trim();
                        // Eğer HH:MM:SS formatındaysa, HH:MM'e çevir
                        const formattedTime = timeValue.includes(':') && timeValue.split(':').length === 3 
                            ? timeValue.substring(0, 5) 
                            : timeValue;
                        formDataToSend.append(key, formattedTime);
                    }
                } else if (key !== 'campaign_image' && formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
                    formDataToSend.append(key, formData[key]);
                }
            });
            
            // Add product_ids if we have any
            if (productIds.length > 0) {
                formDataToSend.append('product_ids', JSON.stringify(productIds));
            }
            if (media) {
                formDataToSend.append('media', media);
            }
            if (campaignImage) {
                formDataToSend.append('campaign_image', campaignImage);
            }

            const url = campaign
                ? `/organizations/${organizationId}/campaigns/${campaign.id}`
                : `/organizations/${organizationId}/campaigns`;

            await apiRequest(url, {
                method: campaign ? 'PUT' : 'POST',
                body: formDataToSend
            });

            onSuccess();
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message || 'Kampanya kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {campaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
                        </h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Title */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                                Kampanya Başlığı *
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                required
                                value={formData.title}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Açıklama
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Campaign Type */}
                        <div>
                            <label htmlFor="campaign_type" className="block text-sm font-medium text-gray-700 mb-2">
                                Kampanya Tipi *
                            </label>
                            <select
                                id="campaign_type"
                                name="campaign_type"
                                required
                                value={formData.campaign_type}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="discount">İndirim</option>
                                <option value="combo">Kombinasyon (Sabit Fiyat)</option>
                                <option value="buy_x_get_y">Al X Get Y</option>
                                <option value="quantity_discount">Adet Bazlı İndirim</option>
                                <option value="combination">Kombinasyon Kampanyası</option>
                                <option value="cross_product">X Alana Y Üründe İndirim</option>
                                <option value="cross_category">X Alana Y Kategorisinde İndirim</option>
                            </select>
                        </div>

                        {/* Discount Type Fields */}
                        {formData.campaign_type === 'discount' && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="discount_type" className="block text-sm font-medium text-gray-700 mb-2">
                                            İndirim Tipi
                                        </label>
                                        <select
                                            id="discount_type"
                                            name="discount_type"
                                            value={formData.discount_type}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="percentage">Yüzde</option>
                                            <option value="fixed_amount">Sabit Tutar</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 mb-2">
                                            İndirim Değeri *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            id="discount_value"
                                            name="discount_value"
                                            required
                                            value={formData.discount_value}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="product_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Ürün
                                        </label>
                                        <SearchableSelect
                                            options={[{ id: '', name: 'Tüm Ürünler' }, ...productsForSelect]}
                                            value={formData.product_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, product_id: value }))}
                                            placeholder="Tüm Ürünler"
                                            showImages={true}
                                            imageKey="image_url"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Kategori
                                        </label>
                                        <SearchableSelect
                                            options={[{ id: '', name: 'Tüm Kategoriler' }, ...categories]}
                                            value={formData.category_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                                            placeholder="Tüm Kategoriler"
                                            showImages={true}
                                            imageKey="image"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Combo Type Fields */}
                        {formData.campaign_type === 'combo' && (
                            <div>
                                <label htmlFor="combo_price" className="block text-sm font-medium text-gray-700 mb-2">
                                    Kombinasyon Fiyatı *
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    id="combo_price"
                                    name="combo_price"
                                    required
                                    value={formData.combo_price}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        )}

                        {/* Buy X Get Y Fields */}
                        {formData.campaign_type === 'buy_x_get_y' && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="buy_quantity" className="block text-sm font-medium text-gray-700 mb-2">
                                            Alınacak Miktar *
                                        </label>
                                        <input
                                            type="number"
                                            id="buy_quantity"
                                            name="buy_quantity"
                                            required
                                            value={formData.buy_quantity}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="get_quantity" className="block text-sm font-medium text-gray-700 mb-2">
                                            Verilecek Miktar *
                                        </label>
                                        <input
                                            type="number"
                                            id="get_quantity"
                                            name="get_quantity"
                                            required
                                            value={formData.get_quantity}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="get_discount_type" className="block text-sm font-medium text-gray-700 mb-2">
                                            Verilecek Ürün Tipi
                                        </label>
                                        <select
                                            id="get_discount_type"
                                            name="get_discount_type"
                                            value={formData.get_discount_type}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="free">Ücretsiz</option>
                                            <option value="percentage">İndirimli (Yüzde)</option>
                                            <option value="fixed_amount">İndirimli (Sabit Tutar)</option>
                                        </select>
                                    </div>
                                    {formData.get_discount_type !== 'free' && (
                                        <div>
                                            <label htmlFor="get_discount_value" className="block text-sm font-medium text-gray-700 mb-2">
                                                İndirim Değeri
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                id="get_discount_value"
                                                name="get_discount_value"
                                                value={formData.get_discount_value}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Quantity Discount Fields */}
                        {formData.campaign_type === 'quantity_discount' && (
                            <>
                                <div>
                                    <label htmlFor="quantity_discount_type" className="block text-sm font-medium text-gray-700 mb-2">
                                        İndirim Hesaplama Tipi *
                                    </label>
                                    <select
                                        id="quantity_discount_type"
                                        name="quantity_discount_type"
                                        value={formData.quantity_discount_type}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="unit_price">Birim Fiyat İndirimi (Her birim için)</option>
                                        <option value="total_price">Toplam Fiyat İndirimi (Tüm adet için)</option>
                                        <option value="buy_x_get_y">X Al Y Bedava</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Adet Kuralları *
                                    </label>
                                    <div className="space-y-2">
                                        {formData.quantity_rules.map((rule, index) => (
                                            <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                                            Ürün *
                                                        </label>
                                                        <SearchableSelect
                                                            options={productsForSelect}
                                                            value={rule.product_id || ''}
                                                            onChange={(value) => {
                                                                const newRules = [...formData.quantity_rules];
                                                                newRules[index] = { ...rule, product_id: parseInt(value) || 0 };
                                                                setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                            }}
                                                            placeholder="Ürün Seçin"
                                                            showImages={true}
                                                            imageKey="image_url"
                                                            labelKey="name"
                                                            valueKey="id"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        placeholder="Min Adet"
                                                        value={rule.min_quantity !== undefined && rule.min_quantity !== null ? rule.min_quantity : ''}
                                                        onChange={(e) => {
                                                            const newRules = [...formData.quantity_rules];
                                                            newRules[index] = { ...rule, min_quantity: parseInt(e.target.value) || 0 };
                                                            setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                        }}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                    />
                                                    {formData.quantity_discount_type === 'unit_price' && (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="Birim Fiyat (₺)"
                                                            value={rule.unit_price !== undefined && rule.unit_price !== null ? rule.unit_price : ''}
                                                            onChange={(e) => {
                                                                const newRules = [...formData.quantity_rules];
                                                                newRules[index] = { ...rule, unit_price: parseFloat(e.target.value) || 0 };
                                                                setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                            }}
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    )}
                                                    {formData.quantity_discount_type === 'total_price' && (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="Toplam Fiyat (₺)"
                                                            value={rule.total_price !== undefined && rule.total_price !== null ? rule.total_price : ''}
                                                            onChange={(e) => {
                                                                const newRules = [...formData.quantity_rules];
                                                                newRules[index] = { ...rule, total_price: parseFloat(e.target.value) || 0 };
                                                                setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                            }}
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        />
                                                    )}
                                                    {formData.quantity_discount_type === 'buy_x_get_y' && (
                                                        <>
                                                            <input
                                                                type="number"
                                                                placeholder="Al (X)"
                                                                value={rule.buy_quantity !== undefined && rule.buy_quantity !== null ? rule.buy_quantity : ''}
                                                                onChange={(e) => {
                                                                    const newRules = [...formData.quantity_rules];
                                                                    newRules[index] = { ...rule, buy_quantity: parseInt(e.target.value) || 0 };
                                                                    setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                                }}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                            />
                                                            <input
                                                                type="number"
                                                                placeholder="Bedava (Y)"
                                                                value={rule.get_quantity !== undefined && rule.get_quantity !== null ? rule.get_quantity : ''}
                                                                onChange={(e) => {
                                                                    const newRules = [...formData.quantity_rules];
                                                                    newRules[index] = { ...rule, get_quantity: parseInt(e.target.value) || 0 };
                                                                    setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                                }}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                            />
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newRules = formData.quantity_rules.filter((_, i) => i !== index);
                                                            setFormData(prev => ({ ...prev, quantity_rules: newRules }));
                                                        }}
                                                        className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                                    >
                                                        Sil
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    quantity_rules: [...prev.quantity_rules, { min_quantity: 0, product_id: 0 }]
                                                }));
                                            }}
                                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500"
                                        >
                                            + Kural Ekle
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Combination Campaign Fields */}
                        {formData.campaign_type === 'combination' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Kombinasyon Ürünleri *
                                    </label>
                                    <div className="space-y-2">
                                        {formData.combination_rules.map((rule, index) => (
                                            <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                                                <div className="flex-1">
                                                    <SearchableSelect
                                                        options={productsForSelect}
                                                        value={rule.product_id || ''}
                                                        onChange={(value) => {
                                                            const newRules = [...formData.combination_rules];
                                                            newRules[index] = { ...rule, product_id: parseInt(value) || 0 };
                                                            setFormData(prev => ({ ...prev, combination_rules: newRules }));
                                                        }}
                                                        placeholder="Ürün Seçin"
                                                        showImages={true}
                                                        imageKey="image_url"
                                                        labelKey="name"
                                                        valueKey="id"
                                                    />
                                                </div>
                                                <input
                                                    type="number"
                                                    placeholder="Min Adet"
                                                    value={rule.min_quantity || ''}
                                                    onChange={(e) => {
                                                        const newRules = [...formData.combination_rules];
                                                        newRules[index] = { ...rule, min_quantity: parseInt(e.target.value) || 1 };
                                                        setFormData(prev => ({ ...prev, combination_rules: newRules }));
                                                    }}
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newRules = formData.combination_rules.filter((_, i) => i !== index);
                                                        setFormData(prev => ({ ...prev, combination_rules: newRules }));
                                                    }}
                                                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                                >
                                                    Sil
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    combination_rules: [...prev.combination_rules, { product_id: 0, min_quantity: 1 }]
                                                }));
                                            }}
                                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500"
                                        >
                                            + Ürün Ekle
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Fiyat/İndirim Seçimi */}
                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Fiyatlandırma Tipi *
                                    </label>
                                    <div className="space-y-3">
                                        <label className="flex items-center space-x-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="combination_pricing_type"
                                                value="fixed_price"
                                                checked={(formData.combo_price && formData.combo_price !== '' && formData.combo_price !== '0') && (!formData.discount_type || formData.discount_type === '')}
                                                onChange={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        combo_price: prev.combo_price || '0', // Keep existing or set default
                                                        discount_type: '',
                                                        discount_value: ''
                                                    }));
                                                }}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">Sabit Fiyat (Kombinasyon için toplam fiyat)</span>
                                        </label>
                                        <label className="flex items-center space-x-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="combination_pricing_type"
                                                value="discount"
                                                checked={(!formData.combo_price || formData.combo_price === '' || formData.combo_price === '0') && (formData.discount_type && formData.discount_type !== '')}
                                                onChange={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        combo_price: '',
                                                        discount_type: prev.discount_type || 'percentage',
                                                        discount_value: prev.discount_value || ''
                                                    }));
                                                }}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">İndirim (Yüzde veya Sabit Tutar)</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Sabit Fiyat */}
                                {formData.combo_price && !formData.discount_type && (
                                    <div>
                                        <label htmlFor="combo_price" className="block text-sm font-medium text-gray-700 mb-2">
                                            Kombinasyon Fiyatı (₺) *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            id="combo_price"
                                            name="combo_price"
                                            required
                                            value={formData.combo_price}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}

                                {/* İndirim */}
                                {!formData.combo_price && formData.discount_type && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="discount_type" className="block text-sm font-medium text-gray-700 mb-2">
                                                İndirim Tipi *
                                            </label>
                                            <select
                                                id="discount_type"
                                                name="discount_type"
                                                required
                                                value={formData.discount_type}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="percentage">Yüzde</option>
                                                <option value="fixed_amount">Sabit Tutar</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 mb-2">
                                                İndirim Değeri *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                id="discount_value"
                                                name="discount_value"
                                                required
                                                value={formData.discount_value}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Cross Product Campaign Fields */}
                        {formData.campaign_type === 'cross_product' && (
                            <>
                                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                    <p className="text-sm text-blue-800">
                                        <strong>X Alana Y Üründe İndirim:</strong> Belirli bir ürün görüntülendiğinde, başka bir üründe indirim uygulanır.
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="trigger_product_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Tetikleyici Ürün (X) *
                                        </label>
                                        <SearchableSelect
                                            options={productsForSelect}
                                            value={formData.trigger_product_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, trigger_product_id: value }))}
                                            placeholder="Tetikleyici Ürün Seçin"
                                            showImages={true}
                                            imageKey="image_url"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="target_product_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Hedef Ürün (Y) *
                                        </label>
                                        <SearchableSelect
                                            options={productsForSelect}
                                            value={formData.target_product_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, target_product_id: value }))}
                                            placeholder="Hedef Ürün Seçin"
                                            showImages={true}
                                            imageKey="image_url"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="discount_type" className="block text-sm font-medium text-gray-700 mb-2">
                                            İndirim Tipi *
                                        </label>
                                        <select
                                            id="discount_type"
                                            name="discount_type"
                                            required
                                            value={formData.discount_type}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="percentage">Yüzde</option>
                                            <option value="fixed_amount">Sabit Tutar</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 mb-2">
                                            İndirim Değeri *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            id="discount_value"
                                            name="discount_value"
                                            required
                                            value={formData.discount_value}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Cross Category Campaign Fields */}
                        {formData.campaign_type === 'cross_category' && (
                            <>
                                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                    <p className="text-sm text-blue-800">
                                        <strong>X Alana Y Kategorisinde İndirim:</strong> Belirli bir ürün/kategori görüntülendiğinde, başka bir kategorideki tüm ürünlerde indirim uygulanır.
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="trigger_product_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Tetikleyici Ürün (X)
                                        </label>
                                        <SearchableSelect
                                            options={productsForSelect}
                                            value={formData.trigger_product_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, trigger_product_id: value }))}
                                            placeholder="Ürün Seçin (Opsiyonel)"
                                            showImages={true}
                                            imageKey="image_url"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="trigger_category_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Tetikleyici Kategori (X)
                                        </label>
                                        <SearchableSelect
                                            options={categories}
                                            value={formData.trigger_category_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, trigger_category_id: value }))}
                                            placeholder="Kategori Seçin (Opsiyonel)"
                                            showImages={true}
                                            imageKey="image"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="target_category_id" className="block text-sm font-medium text-gray-700 mb-2">
                                            Hedef Kategori (Y) *
                                        </label>
                                        <SearchableSelect
                                            options={categories}
                                            value={formData.target_category_id || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, target_category_id: value }))}
                                            placeholder="Hedef Kategori Seçin"
                                            showImages={true}
                                            imageKey="image"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="discount_type" className="block text-sm font-medium text-gray-700 mb-2">
                                            İndirim Tipi *
                                        </label>
                                        <select
                                            id="discount_type"
                                            name="discount_type"
                                            required
                                            value={formData.discount_type}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="percentage">Yüzde</option>
                                            <option value="fixed_amount">Sabit Tutar</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 mb-2">
                                            İndirim Değeri *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            id="discount_value"
                                            name="discount_value"
                                            required
                                            value={formData.discount_value}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Campaign Image (separate from media) */}
                        {(formData.campaign_type === 'combination' || formData.campaign_type === 'quantity_discount' || formData.product_ids?.length > 0) && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Kampanya Görseli (Opsiyonel - Yoksa ürün görselleri gösterilir)
                                </label>
                                <div className="flex items-center space-x-4">
                                    {campaignImagePreview && (
                                        <div className="w-32 h-32 relative">
                                            <img 
                                                src={campaignImagePreview.startsWith('blob:') || campaignImagePreview.startsWith('data:') ? campaignImagePreview : getImageUrl(campaignImagePreview)} 
                                                alt="Campaign Preview" 
                                                className="w-full h-full object-cover rounded-lg"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCampaignImage(null);
                                                    setCampaignImagePreview(null);
                                                }}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setCampaignImage(file);
                                                const reader = new FileReader();
                                                reader.onloadend = () => setCampaignImagePreview(reader.result);
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Media */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Görsel/Video (JPG, GIF, MP4)
                            </label>
                            <div className="flex items-center space-x-4">
                                {mediaPreview && (
                                    <div className="w-32 h-32 relative">
                                        {mediaPreview.startsWith('data:video') || mediaPreview.startsWith('blob:') && mediaPreview.includes('video') || mediaPreview.endsWith('.mp4') ? (
                                            <video 
                                                src={mediaPreview.startsWith('blob:') || mediaPreview.startsWith('data:') ? mediaPreview : getImageUrl(mediaPreview)} 
                                                className="w-full h-full object-cover rounded-lg" 
                                                controls
                                                onError={(e) => {
                                                    console.error('Video preview error:', e);
                                                    setError('Video önizlemesi yüklenemedi.');
                                                }}
                                            />
                                        ) : (
                                            <img 
                                                src={mediaPreview.startsWith('blob:') || mediaPreview.startsWith('data:') ? mediaPreview : getImageUrl(mediaPreview)} 
                                                alt="Preview" 
                                                className="w-full h-full object-cover rounded-lg"
                                                onError={(e) => {
                                                    console.error('Image preview error:', e);
                                                    setError('Görsel önizlemesi yüklenemedi.');
                                                }}
                                            />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMedia(null);
                                                setMediaPreview(null);
                                                const input = document.querySelector('input[type="file"]');
                                                if (input) input.value = '';
                                            }}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={handleMediaChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                            </div>
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                                    Başlangıç Tarihi (Opsiyonel)
                                </label>
                                <input
                                    type="date"
                                    id="start_date"
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">Boş bırakılırsa süresiz aktif olur</p>
                            </div>
                            <div>
                                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                                    Bitiş Tarihi (Opsiyonel)
                                </label>
                                <input
                                    type="date"
                                    id="end_date"
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">Boş bırakılırsa süresiz aktif olur</p>
                            </div>
                        </div>

                        {/* Time Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-2">
                                    Başlangıç Saati
                                </label>
                                <input
                                    type="time"
                                    id="start_time"
                                    name="start_time"
                                    value={formData.start_time}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mb-2">
                                    Bitiş Saati
                                </label>
                                <input
                                    type="time"
                                    id="end_time"
                                    name="end_time"
                                    value={formData.end_time}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Active Days */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Aktif Günler
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                                {days.map(day => (
                                    <label key={day.value} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.active_days.includes(day.value)}
                                            onChange={() => handleDayToggle(day.value)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{day.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                Durum
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="active">Aktif</option>
                                <option value="inactive">Pasif</option>
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CampaignFormModal;

